import React, { FC, useMemo } from 'react'
import { useGetBreakpoint } from '@moonpig/web-core-utils'
import type {
  InspirationType,
  RecentSearchType,
  OnClear,
  OnFocus,
  OnKeyUp,
  OnSubmit,
  ClearButtonAs,
  OnSuggestionsVisible,
  OnRemoveRecentSearch,
  OnClearRecentSearches,
  OnRemindersVisible,
  SuggestionType,
} from './types'
import { NavigationalSuggestionType } from './NavigationalSuggestions/NavigationalSuggestion'
import type { Reminder } from './Reminders/Reminder'
import { DepartmentType, departmentMap } from './departmentMap'
import { SearchCombobox } from './Combobox/SearchCombobox'
import { MAX_SUGGESTIONS } from './constants'
import { SearchForm } from './SearchForm'

type SearchProps = {
  items: SuggestionType[]
  navigationalSuggestions?: NavigationalSuggestionType[]
  inspirations?: InspirationType[]
  recentSearches?: RecentSearchType[]
  reminders?: Reminder[]
  onSubmit: OnSubmit
  onTabChange: (tab: string) => void
  onKeyUp: OnKeyUp
  onFocus: OnFocus
  onSuggestionsVisible: OnSuggestionsVisible
  onRemoveRecentSearch: OnRemoveRecentSearch
  onClearRecentSearches: OnClearRecentSearches
  onRemindersVisible: OnRemindersVisible
  suggestionsLoading?: boolean
  onClear: OnClear
  clearButtonAs?: ClearButtonAs
  searchTerm?: string
  maxSuggestions?: number
  maxNavigationalSuggestions?: number
  navigationalHeaderText?: string
  searchDepartment: {
    name: string
    title: string
  }
}

export const Search: FC<React.PropsWithChildren<SearchProps>> = ({
  items,
  onSubmit,
  onTabChange,
  onKeyUp,
  onFocus,
  onClear,
  clearButtonAs,
  searchTerm = '',
  onSuggestionsVisible,
  onRemoveRecentSearch,
  onClearRecentSearches,
  onRemindersVisible,
  maxSuggestions = MAX_SUGGESTIONS,
  navigationalSuggestions = [],
  maxNavigationalSuggestions,
  navigationalHeaderText,
  inspirations,
  recentSearches = [],
  reminders = [],
  suggestionsLoading,
  searchDepartment,
}) => {
  const formRef = React.useRef<HTMLFormElement | null>(null)
  const searchInput = React.useRef<HTMLInputElement>(null)
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  const [activeDepartment, setDepartment] = React.useState<DepartmentType>(
    departmentMap.All,
  )
  const [popoverWidth, setPopoverWidth] = React.useState<number | undefined>()
  const currentBreakpoint = useGetBreakpoint()

  React.useEffect(() => {
    if (!formRef.current) return

    const updateWidth = () => {
      if (formRef.current) {
        setPopoverWidth(formRef.current.offsetWidth)
      }
    }

    updateWidth()

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  const isMobile = useMemo(() => {
    return currentBreakpoint === 'xs'
  }, [currentBreakpoint])

  const setActiveDepartment = (selectedDepartment: DepartmentType) => {
    onTabChange(selectedDepartment.label.default)
    setDepartment(selectedDepartment)
  }

  const comboboxProps = {
    isSearchOpen,
    setIsSearchOpen,
    activeDepartment,
    setActiveDepartment,
    searchInput,
    popoverWidth,
    navigationalSuggestions,
    navigationalHeaderText,
    suggestionsLoading,
    onSuggestionsVisible,
    onRemindersVisible,
    onRemoveRecentSearch,
    onClearRecentSearches,
    items,
    inspirations,
    recentSearches,
    reminders,
    maxSuggestions,
    maxNavigationalSuggestions,
    searchDepartment,
    onSubmit,
    onTabChange,
    onKeyUp,
    onFocus,
    onClear,
    clearButtonAs,
    searchTerm,
    isMobile,
  }

  return (
    <SearchForm
      ref={formRef}
      isActive={isSearchOpen}
      onFocus={() => {
        setIsSearchOpen(true)
      }}
    >
      <SearchCombobox {...comboboxProps} />
    </SearchForm>
  )
}
